import { forwardRef, useEffect, useRef, useState, type HTMLAttributes, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { bottomSheetOverlayVariants, bottomSheetContentVariants, type BottomSheetContentProps } from './variants';

export interface BottomSheetProps extends HTMLAttributes<HTMLDivElement>, BottomSheetContentProps {
  /** Whether the sheet is visible. */
  open: boolean;
  /** Called when the user clicks the backdrop, presses Escape, or drags the sheet down past the threshold. */
  onClose: () => void;
  /** Hide the drag-handle pill at the top of the sheet. */
  hideHandle?: boolean;
  /**
   * Float the drag handle OVER the content instead of stacking above it.
   * For full-bleed sheets (content painting its own header up to the very top
   * edge) — the default in-flow handle strip would show as a bare surface band
   * before the content starts. Drag-to-dismiss behaves identically.
   */
  handleOverlay?: boolean;
}

/** Drag distance (in pixels) past which a pointer-up dismisses the sheet. */
const DISMISS_THRESHOLD = 120;

/** Duration of the inline transform transition used to close the sheet. */
const EXIT_DURATION_MS = 250;

/**
 * Mobile-style sheet that slides up from the bottom of the viewport.
 *
 * Renders via a portal with a fading backdrop, an Escape-to-close handler,
 * body scroll-lock while open, and pointer-driven drag-to-dismiss on the
 * drag handle. The slide-up entrance animation is CSS-only.
 *
 * @example
 * ```tsx
 * <BottomSheet open={isOpen} onClose={() => setOpen(false)}>
 *   <h2>Sign up</h2>
 * </BottomSheet>
 * ```
 */
const BottomSheet = forwardRef<HTMLDivElement, BottomSheetProps>(
  ({ open, onClose, size, hideHandle, handleOverlay, className, children, ...props }, ref) => {
    const [dragY, setDragY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [shouldRender, setShouldRender] = useState(open);
    const dragStartY = useRef<number | null>(null);

    // Derive `shouldRender` from `open` during render: when the sheet opens,
    // we need to mount it before the next paint so the entrance animation can
    // start. https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    if (open && !shouldRender) {
      setShouldRender(true);
    }

    // Unmount lags `open=false` by EXIT_DURATION_MS so the exit transition can play.
    useEffect(() => {
      if (open) return;
      const t = window.setTimeout(() => {
        setShouldRender(false);
        setDragY(0);
      }, EXIT_DURATION_MS);
      return () => window.clearTimeout(t);
    }, [open]);

    const isClosing = !open && shouldRender;

    useEffect(() => {
      if (!open) return;

      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      function handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Escape') onClose();
      }
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = prev;
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [open, onClose]);

    function handlePointerDown(e: PointerEvent<HTMLDivElement>) {
      dragStartY.current = e.clientY;
      setIsDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    function handlePointerMove(e: PointerEvent<HTMLDivElement>) {
      if (dragStartY.current === null) return;
      const delta = e.clientY - dragStartY.current;
      setDragY(Math.max(0, delta));
    }

    function handlePointerUp(e: PointerEvent<HTMLDivElement>) {
      if (dragStartY.current === null) return;
      const delta = e.clientY - dragStartY.current;
      dragStartY.current = null;
      setIsDragging(false);
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      if (delta > DISMISS_THRESHOLD) {
        // Leave `dragY` at the released position so the exit transition can
        // start from where the user let go (not snap to 0 first).
        onClose();
      } else {
        setDragY(0);
      }
    }

    if (!shouldRender) return null;

    // While closing, transition the sheet from its current Y (drag position or 0)
    // out to translateY(100%). Using an inline transition — not the keyframe
    // animation — preserves the drag-release position as the starting point.
    const contentStyle = isClosing
      ? {
          transform: 'translateY(100%)',
          transition: `transform ${EXIT_DURATION_MS}ms cubic-bezier(0.4, 0, 1, 1)`,
        }
      : dragY > 0 || isDragging
        ? {
            transform: `translateY(${dragY}px)`,
            transition: isDragging ? 'none' : 'transform 200ms ease-out',
          }
        : undefined;

    // Only animate the entrance when the sheet is fully at rest — never while the user is dragging.
    const animateEntry = !isClosing && !isDragging && dragY === 0;

    return createPortal(
      // data-overlay-root lets useClickOutside treat this layer as stacked
      // above any open popover (see the hook's doc comment).
      <div data-overlay-root className={cn(bottomSheetOverlayVariants(), isClosing && 'animate-fade-out')} onClick={onClose}>
        <div
          ref={ref}
          className={cn(bottomSheetContentVariants({ size }), animateEntry && 'animate-slide-up', className)}
          style={contentStyle}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          {...props}
        >
          {!hideHandle && (
            <div
              data-testid="bottom-sheet-handle"
              className={cn(
                'flex justify-center mb-3 -mx-[14px] -mt-2 px-[14px] pt-2 pb-2 cursor-grab active:cursor-grabbing touch-none select-none',
                handleOverlay && 'absolute inset-x-0 top-0 z-10 mx-0 mt-0 mb-0 pt-3',
              )}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <div className="h-1 w-12 rounded-full bg-border" aria-hidden="true" />
            </div>
          )}
          {children}
        </div>
      </div>,
      document.body,
    );
  },
);
BottomSheet.displayName = 'BottomSheet';

export { BottomSheet };
